import React, { useState, useRef } from "react";
import {
  Container,
  Header,
  SpaceBetween,
  FormField,
  Select,
  Button,
  Box,
  Alert,
  ProgressBar,
  ExpandableSection,
  ColumnLayout,
  Badge,
} from "@cloudscape-design/components";
import { optimizerApi } from "./ListingCreationApi.js";

const MAX_FILE_SIZE = 10485760; // 10 MB
const VALID_EXTENSIONS = [".xlsx", ".xls", ".csv"];

const CATEGORY_OPTIONS = [
  { value: "fashion-apparel", label: "Fashion & Apparel" },
  { value: "beauty-personal-care", label: "Beauty & Personal Care" },
  { value: "electronics", label: "Electronics" },
  { value: "home-kitchen", label: "Home & Kitchen" },
  { value: "food-beverages", label: "Food & Beverages" },
  { value: "health-wellness", label: "Health & Wellness" },
  { value: "baby-kids", label: "Baby & Kids" },
  { value: "sports-fitness", label: "Sports & Fitness" },
  { value: "jewelry-accessories", label: "Jewelry & Accessories" },
  { value: "bags-luggage-travel", label: "Bags, Luggage & Travel" },
  { value: "pet-care", label: "Pet Care" },
  { value: "eyewear", label: "Eyewear" },
  { value: "stationery-office", label: "Stationery & Office Supplies" },
  { value: "automotive-accessories", label: "Automotive & Car Accessories" },
  { value: "garden-outdoor", label: "Garden & Outdoor" },
  { value: "general", label: "General (use if unsure)" },
];

// Content type map for S3 upload
const CONTENT_TYPE_MAP = {
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".xls": "application/vnd.ms-excel",
  ".csv": "text/csv",
};

export default function ListingOptimizer({ username, onJobComplete }) {
  // State machine: idle → detecting → previewing → processing → completed | failed
  const [phase, setPhase] = useState("idle");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const [runId, setRunId] = useState(null);

  // Category + Preview state
  const [detectedCategory, setDetectedCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [allVibes, setAllVibes] = useState([]);
  const [selectedVibeIndex, setSelectedVibeIndex] = useState(null);
  const [previews, setPreviews] = useState([]);
  const [originalTitle, setOriginalTitle] = useState("");
  const [selectedPreviewIndex, setSelectedPreviewIndex] = useState(null);

  // Processing state
  const [logs, setLogs] = useState("Waiting for logs...");
  const [status, setStatus] = useState(null);

  // Total rows in the uploaded file (returned by detect-category) and the
  // server-side cap. When totalRowsInFile > maxRows we show a non-blocking
  // warning before the user clicks "Generate All".
  const [totalRowsInFile, setTotalRowsInFile] = useState(null);
  const [maxRows, setMaxRows] = useState(1000);

  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  // ─── File Validation ──────────────────────────────────────────────────
  const validateFile = (selectedFile) => {
    const ext = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf("."));
    if (!VALID_EXTENSIONS.includes(ext)) {
      setError("Invalid file type. Accepted: .xlsx, .xls, .csv");
      return false;
    }
    if (selectedFile.size >= MAX_FILE_SIZE) {
      setError("File size must be less than 10 MB");
      return false;
    }
    return true;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    if (validateFile(selectedFile)) {
      setFile(selectedFile);
      setError(null);
    } else {
      setFile(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const selectedFile = e.dataTransfer.files?.[0];
    if (!selectedFile) return;
    if (validateFile(selectedFile)) {
      setFile(selectedFile);
      setError(null);
    } else {
      setFile(null);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const handleZoneClick = () => { fileInputRef.current?.click(); };

  // ─── Upload + Detect Category ─────────────────────────────────────────
  const handleUploadAndDetect = async () => {
    if (!file) {
      setError("Please select a file to upload");
      return;
    }
    setError(null);
    setPhase("detecting");

    try {
      // Step 1: Get presigned upload URL
      const { run_id, upload_url } = await optimizerApi.getUploadUrl(file.name, username);
      setRunId(run_id);

      // Step 2: Upload file to S3
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
      const contentType = CONTENT_TYPE_MAP[ext] || "application/octet-stream";
      await optimizerApi.uploadFileToS3(upload_url, file, contentType);

      // Step 3: Detect category
      const categoryResult = await optimizerApi.detectCategory(run_id);
      setDetectedCategory(categoryResult);
      setSelectedCategory(
        CATEGORY_OPTIONS.find((o) => o.value === categoryResult.category) || CATEGORY_OPTIONS[15]
      );

      // Capture the row count + cap from the detect response so we can show
      // a truncation warning before the user kicks off processing.
      if (typeof categoryResult.total_rows_in_file === "number") {
        setTotalRowsInFile(categoryResult.total_rows_in_file);
      }
      if (typeof categoryResult.max_rows === "number") {
        setMaxRows(categoryResult.max_rows);
      }

      // Store all vibes for the detected category
      const vibeOptions = (categoryResult.vibes || []).map((v, i) => ({
        value: String(i + 1),
        label: `${i + 1}. ${v}`,
      }));
      setAllVibes(vibeOptions);

      // Step 4: Generate previews
      const previewResult = await optimizerApi.generatePreviews(run_id, categoryResult.category);
      setPreviews(previewResult.previews || []);
      setOriginalTitle(previewResult.original_title || "");
      setPhase("previewing");
    } catch (err) {
      setError(err.message);
      setPhase("failed");
    }
  };

  // ─── Category Override + Re-preview ───────────────────────────────────
  const handleCategoryChange = async ({ detail }) => {
    setSelectedCategory(detail.selectedOption);
    setSelectedPreviewIndex(null);
    setSelectedVibeIndex(null);
    setPreviews([]);
    setPhase("detecting");

    try {
      const previewResult = await optimizerApi.generatePreviews(runId, detail.selectedOption.value);
      setPreviews(previewResult.previews || []);
      setOriginalTitle(previewResult.original_title || "");

      // Update vibes dropdown for the new category
      const vibeOptions = (previewResult.vibes || []).map((v, i) => ({
        value: String(i + 1),
        label: `${i + 1}. ${v}`,
      }));
      setAllVibes(vibeOptions);

      setPhase("previewing");
    } catch (err) {
      setError(err.message);
      setPhase("failed");
    }
  };

  // ─── Start Processing ─────────────────────────────────────────────────
  const handleGenerateAll = async () => {
    // Determine which vibe index to use: dropdown selection takes priority, then preview card
    let vibeIndex = null;
    if (selectedVibeIndex !== null) {
      vibeIndex = parseInt(selectedVibeIndex, 10);
    } else if (selectedPreviewIndex !== null) {
      vibeIndex = previews[selectedPreviewIndex].vibe_index;
    }

    if (vibeIndex === null) return;

    setError(null);
    setPhase("processing");
    setLogs("Waiting for logs...");
    setStatus(null);

    try {
      await optimizerApi.startProcessing(runId, vibeIndex);

      // Poll status + logs every 2s
      pollRef.current = setInterval(async () => {
        try {
          const [statusRes, logsRes] = await Promise.allSettled([
            optimizerApi.getStatus(runId),
            optimizerApi.getLogs(runId),
          ]);

          if (statusRes.status === "fulfilled") {
            setStatus(statusRes.value);
            if (statusRes.value.status === "completed") {
              clearInterval(pollRef.current);
              setPhase("completed");
              if (onJobComplete) onJobComplete();
            } else if (statusRes.value.status === "failed") {
              clearInterval(pollRef.current);
              setError(statusRes.value.error || "Processing failed");
              setPhase("failed");
            }
          }

          if (logsRes.status === "fulfilled") {
            setLogs(logsRes.value.logs || "Waiting for logs...");
          }
        } catch (e) {
          // Ignore polling errors
        }
      }, 2000);
    } catch (err) {
      setError(err.message);
      setPhase("failed");
    }
  };

  // ─── Download ─────────────────────────────────────────────────────────
  const handleDownload = async () => {
    try {
      await optimizerApi.downloadOutput(runId);
    } catch (err) {
      setError(err.message);
    }
  };

  // ─── Reset ────────────────────────────────────────────────────────────
  const handleReset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setPhase("idle");
    setFile(null);
    setError(null);
    setRunId(null);
    setDetectedCategory(null);
    setSelectedCategory(null);
    setAllVibes([]);
    setSelectedVibeIndex(null);
    setPreviews([]);
    setOriginalTitle("");
    setSelectedPreviewIndex(null);
    setLogs("Waiting for logs...");
    setStatus(null);
    setTotalRowsInFile(null);
    setMaxRows(1000);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ─── Progress from logs ─────────────────────────────────────────────
  const getProgress = () => {
    if (phase === "completed") return 100;
    if (!logs || logs === "Waiting for logs...") return 5;

    // Parse "X/Y products processed" from logs
    const matches = [...logs.matchAll(/(\d+)\/(\d+) products processed/g)];
    if (matches.length > 0) {
      const lastMatch = matches[matches.length - 1];
      const processed = parseInt(lastMatch[1], 10);
      const total = parseInt(lastMatch[2], 10);
      if (total > 0) return Math.round((processed / total) * 100);
    }

    // Fallback: count batch completions
    const batchMatches = [...logs.matchAll(/Batch (\d+)\/(\d+)/g)];
    if (batchMatches.length > 0) {
      const lastBatch = batchMatches[batchMatches.length - 1];
      const done = parseInt(lastBatch[1], 10);
      const total = parseInt(lastBatch[2], 10);
      if (total > 0) return Math.min(95, Math.round((done / total) * 100));
    }

    if (logs.includes("Starting batch")) return 10;
    return 5;
  };

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <SpaceBetween size="l">
      {error && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step 1: Upload */}
      {(phase === "idle" || phase === "detecting") && (
        <Container header={<Header variant="h2">Upload Product File</Header>}>
          <SpaceBetween size="m">
            <Alert type="info">
              Upload your product catalogue (Excel or CSV) with columns for Product ID, Title, and Description.
              The optimizer will rewrite all listings in a D2C brand voice.
            </Alert>

            <Box margin={{ top: "s" }}>
              <Box variant="awsui-key-label" margin={{ bottom: "xxs" }}>Product File</Box>
              <Box fontSize="body-s" color="text-body-secondary" margin={{ bottom: "xs" }}>
                .xlsx, .xls, or .csv — max 10 MB, up to 1000 products
              </Box>
              <div
                role="button"
                tabIndex={0}
                className={`image-link-drop-zone ${dragOver ? "drag-over" : ""}`}
                onClick={handleZoneClick}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleZoneClick(); }}
                aria-label="Drop file here or click to browse"
              >
                <p className="drop-zone-icon">📊</p>
                <p className="drop-zone-title">
                  {file ? file.name : "Drag & drop your product file here, or click to select"}
                </p>
                <p className="drop-zone-hint">
                  {file ? `${(file.size / 1024).toFixed(1)} KB` : "Excel or CSV with Product ID, Title, Description columns"}
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </Box>

            <Button
              variant="primary"
              loading={phase === "detecting"}
              onClick={handleUploadAndDetect}
              disabled={!file || phase === "detecting"}
            >
              {phase === "detecting" ? "Detecting Category..." : "Upload & Detect Category"}
            </Button>
          </SpaceBetween>
        </Container>
      )}

      {/* Step 2: Category + Preview */}
      {(phase === "previewing") && (
        <Container header={<Header variant="h2">Choose Your Brand Style</Header>}>
          <SpaceBetween size="m">
            <ColumnLayout columns={2}>
              <FormField label="Detected Category">
                <Select
                  selectedOption={selectedCategory}
                  onChange={handleCategoryChange}
                  options={CATEGORY_OPTIONS}
                  placeholder="Select category"
                />
              </FormField>
              <Box>
                <Box variant="awsui-key-label">Original Title (first product)</Box>
                <Box color="text-body-secondary" fontSize="body-s" margin={{ top: "xxs" }}>
                  {originalTitle || "—"}
                </Box>
              </Box>
            </ColumnLayout>

            <Box variant="awsui-key-label" margin={{ top: "s" }}>
              Pick a style — here's how your first product looks in 3 different brand voices:
            </Box>

            <div className="optimizer-preview-grid">
              {previews.map((preview, idx) => (
                <div
                  key={idx}
                  className={`optimizer-preview-card ${selectedPreviewIndex === idx ? "selected" : ""}`}
                  onClick={() => { setSelectedPreviewIndex(idx); setSelectedVibeIndex(null); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setSelectedPreviewIndex(idx); setSelectedVibeIndex(null); } }}
                  aria-label={`Select style: ${preview.vibe_name}`}
                >
                  <div className="preview-vibe-name">{preview.vibe_name}</div>
                  <div className="preview-brand">{preview.reference_brand}</div>
                  <div className="preview-title">{preview.new_title}</div>
                  <div className="preview-desc">
                    {(preview.new_description || "").replace(/<[^>]*>/g, "").slice(0, 100)}
                    {(preview.new_description || "").length > 100 ? "…" : ""}
                  </div>
                </div>
              ))}
            </div>

            <FormField
              label="Or choose from all available brand vibes"
              description="Select any vibe from the full list if the previews above don't match your brand"
            >
              <Select
                selectedOption={selectedVibeIndex ? allVibes.find((v) => v.value === selectedVibeIndex) : null}
                onChange={({ detail }) => { setSelectedVibeIndex(detail.selectedOption.value); setSelectedPreviewIndex(null); }}
                options={allVibes}
                placeholder="Browse all brand vibes..."
                filteringType="auto"
              />
            </FormField>

            <SpaceBetween size="s" direction="horizontal">
              <Button
                variant="primary"
                onClick={handleGenerateAll}
                disabled={selectedPreviewIndex === null && selectedVibeIndex === null}
              >
                ✨ Generate All
              </Button>
              <Button onClick={handleReset}>Start Over</Button>
            </SpaceBetween>
          </SpaceBetween>
        </Container>
      )}

      {/* Truncation warning: shown above the action buttons in the preview
          step when the uploaded file has more rows than the server-side
          cap. Non-blocking — the user can still click Generate All; the
          backend will process only the first `maxRows` rows. */}
      {phase === "previewing" && totalRowsInFile !== null && totalRowsInFile > maxRows && (
        <Alert type="warning" header="File exceeds row limit">
          Your file has <strong>{totalRowsInFile.toLocaleString()} products</strong> but the optimizer
          processes a maximum of <strong>{maxRows.toLocaleString()}</strong> per run.
          Only the first {maxRows.toLocaleString()} rows will be optimized — the rest will be skipped.
          To process them, run them in another batch.
        </Alert>
      )}

      {/* Step 3: Processing + Result */}
      {(phase === "processing" || phase === "completed" || phase === "failed") && (
        <Container header={<Header variant="h2">⚙️ Processing</Header>}>
          <SpaceBetween size="m">
            <ColumnLayout columns={4} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Status</Box>
                <div>
                  <Badge color={phase === "completed" ? "green" : phase === "failed" ? "red" : "blue"}>
                    {phase === "completed" ? "COMPLETED" : phase === "failed" ? "FAILED" : "PROCESSING"}
                  </Badge>
                </div>
              </div>
              <div>
                <Box variant="awsui-key-label">Products</Box>
                <Box>{status?.total_rows || status?.success_count || "—"}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Succeeded</Box>
                <Box>{status?.success_count ?? "—"}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Failed</Box>
                <Box>{status?.failed_count ?? "—"}</Box>
              </div>
            </ColumnLayout>

            {phase === "processing" && (
              <ProgressBar
                value={getProgress()}
                additionalInfo={`Optimizing listings... ${getProgress()}%`}
                status="in-progress"
              />
            )}

            <ExpandableSection headerText="💻 Processing Logs" defaultExpanded={true}>
              <Box padding="s">
                <pre className="listing-logs-container">{logs}</pre>
              </Box>
            </ExpandableSection>

            {phase === "completed" && (
              <SpaceBetween size="s" direction="horizontal">
                <Button variant="primary" iconName="download" onClick={handleDownload}>
                  Download Optimized File
                </Button>
                <Button onClick={handleReset}>Optimize Another File</Button>
              </SpaceBetween>
            )}

            {phase === "failed" && (
              <Button onClick={handleReset}>Try Again</Button>
            )}
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
}
